import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Gift, GraduationCap, KeyRound, Loader2, Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { VImageUpload } from "@/components/vendor/VImageUpload";
import { AdminCard, AdminModal, Field, Pill, Td, Th, TableWrap, inputCls, textareaCls } from "@/pages/admin/ui";

const empty = { title: "", description: "", image_url: "", badge: "", product_ids: [], formation_ids: [], price: 0, status: "draft" };

function BundleForm({ open, onClose, editing, options, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title, description: editing.description || "", image_url: editing.image_url || "",
        badge: editing.badge || "", price: editing.price || 0, status: editing.status || "draft",
        product_ids: editing.products.map((p) => p.id), formation_ids: editing.formations.map((f) => f.id),
      });
    } else setForm(empty);
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleId = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  const value = [
    ...options.products.filter((p) => form.product_ids.includes(p.id)),
    ...options.formations.filter((f) => form.formation_ids.includes(f.id)),
  ].reduce((s, x) => s + (x.price || 0), 0);
  const savings = Math.max(0, value - Number(form.price || 0));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titre obligatoire"); return; }
    if (form.product_ids.length + form.formation_ids.length === 0) { toast.error("Ajoutez au moins un article"); return; }
    setSaving(true);
    const payload = { ...form, price: Number(form.price) || 0 };
    try {
      if (editing) await api.put(`/vendor/bundles/${editing.id}`, payload);
      else await api.post("/vendor/bundles", payload);
      toast.success(editing ? "Offre mise à jour" : "Offre groupée créée");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={editing ? "Modifier l'offre" : "Créer une offre groupée"} wide>
      <div className="space-y-4">
        <Field label="Titre du pack"><input data-testid="vbundle-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
        <Field label="Description"><textarea className={textareaCls} rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Badge (optionnel)"><input className={inputCls} value={form.badge} onChange={(e) => set("badge", e.target.value)} placeholder="Ex : Meilleure offre" /></Field>
          <Field label="Prix du pack (FCFA)"><input data-testid="vbundle-price" type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} /></Field>
        </div>
        <VImageUpload label="Image du pack" value={form.image_url} onChange={(v) => set("image_url", v)} testId="vbundle-image" />

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><KeyRound className="h-3.5 w-3.5" /> Mes produits ({form.product_ids.length})</p>
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-xl border border-line p-2">
              {options.products.length === 0 && <p className="p-2 text-xs text-slate-400">Aucun produit publié.</p>}
              {options.products.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <input type="checkbox" className="accent-brand" checked={form.product_ids.includes(p.id)} onChange={() => toggleId("product_ids", p.id)} data-testid={`vbundle-product-${p.id}`} />
                  <span className="flex-1 truncate text-sm text-slate-600">{p.name}</span>
                  <span className="text-xs text-slate-400">{formatPrice(p.price)}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><GraduationCap className="h-3.5 w-3.5" /> Mes formations ({form.formation_ids.length})</p>
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-xl border border-line p-2">
              {options.formations.length === 0 && <p className="p-2 text-xs text-slate-400">Aucune formation publiée.</p>}
              {options.formations.map((f) => (
                <label key={f.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <input type="checkbox" className="accent-brand" checked={form.formation_ids.includes(f.id)} onChange={() => toggleId("formation_ids", f.id)} data-testid={`vbundle-formation-${f.id}`} />
                  <span className="flex-1 truncate text-sm text-slate-600">{f.title}</span>
                  <span className="text-xs text-slate-400">{formatPrice(f.price)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-surface px-4 py-3 text-sm">
          <span className="text-slate-500">Valeur totale : <b className="text-ink">{formatPrice(value)}</b></span>
          <span className="font-bold text-emerald-600">Économie client : {formatPrice(savings)}</span>
        </div>

        <Field label="Statut">
          <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="vbundle-status">
            <option value="draft">Brouillon</option><option value="published">Publiée</option>
          </select>
        </Field>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="vbundle-save" className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Enregistrer" : "Créer l'offre"}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

export default function VendorBundles() {
  const [bundles, setBundles] = useState([]);
  const [options, setOptions] = useState({ products: [], formations: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, o] = await Promise.all([api.get("/vendor/bundles"), api.get("/vendor/bundle-options")]);
      setBundles(b.data); setOptions(o.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (b, status) => { try { await api.put(`/vendor/bundles/${b.id}/status`, { status }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (b) => { if (window.confirm(`Supprimer l'offre « ${b.title} » ?`)) { try { await api.delete(`/vendor/bundles/${b.id}`); toast.success("Offre supprimée"); load(); } catch (e) { toast.error(apiError(e)); } } };

  return (
    <div className="space-y-5" data-testid="vendor-bundles">
      <AdminCard
        title="Offres groupées"
        description="Combinez VOS produits et VOS formations en packs à prix réduit — visibles dans votre boutique."
        action={<button onClick={() => setModal({ editing: null })} data-testid="add-vbundle-btn" className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white"><Plus className="h-4 w-4" /> Créer une offre</button>}
      >
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <TableWrap>
            <table className="w-full text-left">
              <thead><tr className="border-b border-line"><Th>Offre</Th><Th>Contenu</Th><Th>Valeur</Th><Th>Prix</Th><Th>Économie</Th><Th>Statut</Th><Th className="text-right">Actions</Th></tr></thead>
              <tbody>
                {bundles.map((b) => (
                  <tr key={b.id} className="border-b border-line/70" data-testid={`vbundle-row-${b.slug}`}>
                    <Td>
                      <div className="flex items-center gap-2.5">
                        {b.image_url ? <img src={b.image_url} alt="" className="h-10 w-16 rounded-lg object-cover" /> : <span className="flex h-10 w-16 items-center justify-center rounded-lg bg-brand-light text-brand"><Gift className="h-5 w-5" /></span>}
                        <span className="font-bold text-ink">{b.title}</span>
                      </div>
                    </Td>
                    <Td><span className="text-xs text-slate-500">{b.products.length} produit(s) • {b.formations.length} formation(s)</span></Td>
                    <Td><span className="text-slate-400 line-through">{formatPrice(b.value)}</span></Td>
                    <Td className="font-bold text-brand">{formatPrice(b.price)}</Td>
                    <Td>{b.savings > 0 ? <span className="flex items-center gap-1 text-xs font-bold text-emerald-600"><Tag className="h-3.5 w-3.5" /> {formatPrice(b.savings)}</span> : <span className="text-xs text-slate-400">—</span>}</Td>
                    <Td><Pill tone={b.status === "published" ? "green" : "amber"}>{b.status === "published" ? "Publiée" : "Brouillon"}</Pill></Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setModal({ editing: b })} data-testid={`edit-vbundle-${b.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-brand-soft hover:text-brand"><Pencil className="h-4 w-4" /></button>
                        {b.status === "published" ? (
                          <button onClick={() => setStatus(b, "draft")} data-testid={`unpublish-vbundle-${b.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"><EyeOff className="h-4 w-4" /></button>
                        ) : (
                          <button onClick={() => setStatus(b, "published")} data-testid={`publish-vbundle-${b.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><Eye className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => del(b)} data-testid={`delete-vbundle-${b.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bundles.length === 0 && <p className="py-12 text-center text-sm text-slate-500">Aucune offre. Cliquez sur « Créer une offre ».</p>}
          </TableWrap>
        )}
      </AdminCard>

      {modal && <BundleForm open onClose={() => setModal(null)} editing={modal.editing} options={options} onSaved={load} />}
    </div>
  );
}
